import React, { useEffect } from "react"
import { Outlet, Link, useNavigate, useRouterState } from "@tanstack/react-router"
import { useCurrentAdmin, useAdminLogout, useAdminDocuments } from "@/lib/queries"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { LogOut } from "lucide-react"
import { Footer } from "@/components/Footer"
import { hasPermission, PERMISSIONS } from "@/lib/permissions"

const navItems = [
  { label: "Home", to: "/admin" },
  { label: "Clinical Reviews", to: "/admin/clinical-reviews" },
  { label: "Workflows", to: "/admin/workflows" },
  { label: "Supplemental", to: "/admin/supplemental" },
]

export function AdminLayout() {
  const { data: admin, isLoading, isError } = useCurrentAdmin()
  const logout = useAdminLogout()
  const navigate = useNavigate()
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const { data: staticPages = [] } = useAdminDocuments("static_page")

  useEffect(() => {
    if (!isLoading && (isError || !admin)) {
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      navigate({ to: "/admin/login", search: { redirect: currentUrl } })
    }
  }, [isLoading, isError, admin, navigate])

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>
  }

  if (isError || !admin) {
    return null
  }

  return (
    <div className="flex h-screen flex-col">
      {/* Top navigation bar */}
      <header className="flex h-14 shrink-0 items-center border-b bg-background px-6">
        {/* Logo */}
        <Link to="/admin" className="mr-8 flex items-center">
          <img
            src="/signature_logo.png"
            alt="Specia"
            className="h-7 w-auto object-contain"
          />
        </Link>

        {/* Nav links */}
        <nav className="flex items-center gap-1">
          {navItems.map((item) => {
            const isActive =
              item.to === "/admin"
                ? currentPath === "/admin"
                : currentPath.startsWith(item.to)
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>

        {/* Right side: users, content, logout */}
        <div className="ml-auto flex items-center gap-3">
          <nav className="flex items-center gap-1">
            {hasPermission(admin, PERMISSIONS.MANAGE_USERS) && (
              <Link
                to="/admin/users"
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  currentPath.startsWith("/admin/users")
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                Users
              </Link>
            )}
            <Link
              to="/admin/content"
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                currentPath.startsWith("/admin/content")
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              Content
            </Link>
          </nav>
          <span className="text-sm text-muted-foreground">
            {admin.email}
          </span>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" title="Logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Log out</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to log out?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    logout.mutate(undefined, {
                      onSuccess: () => navigate({ to: "/admin/login" }),
                    })
                  }}
                >
                  Log out
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </header>

      {/* Main content area */}
      <main className="flex-1 overflow-auto min-h-0">
        <Outlet />
      </main>

      <Footer
        pathPrefix="/admin"
        isAdmin
        footerLinks={staticPages
          .filter((d) => d.show_in_footer)
          .map((d) => ({ title: d.title, slug: d.slug }))}
      />
    </div>
  )
}
